/**
 * 押し下げで表示を切り替える 1 つのボタン。比較（`DiffCompareToggle`）・一覧の絞り込み
 * （`PjChangedOnlyToggle`）・フロー図の全画面（`FlowFullscreenToggle`）が同じ形で描く。
 *
 * **文言は「押すと起きること」を書き、押し下げで入れ替える**（Dhik 2026-09-29「押したときに
 * 起きることをラベルに書いて切り替えてほしい。今だと変更前と比較状態からPJリリース後の状態に
 * 切り替わることがわからない」）。塗りと `aria-pressed` だけで状態を示すと、押した後に
 * もう一度押すと何に戻るのかが画面から読めない。塗り（押下中は primary）と `aria-pressed` は
 * 残し、読み上げでも「いま押されているか」が分かるようにする。
 *
 * **形は 1 か所に置く。** 画面ごとに同じ操作を別の形（2 択の並び / 色だけの押し下げ）で描くと、
 * 読み手は画面を移るたびに操作の読み方を覚え直すことになる（同日「UIに一貫性がない」）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 押し下げで表示を切り替えるボタンを、押すと起きることを書いた文言・塗り・aria-pressed で全画面同じ形に描く。
 * @graph-connects none
 */
import { Button } from '@cortex/web-design-system/components/ui/Button';
import type { ButtonHTMLAttributes } from 'react';

/** `StateToggleButton` の props。 */
export interface StateToggleButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onClick' | 'aria-pressed' | 'type'
> {
  /** いま押されているか。`disabled` のときは押されていない扱いにする。 */
  pressed: boolean;
  /** 押されていないときの文言（押すと起きること）。 */
  idleLabel: string;
  /** 押されているときの文言（もう一度押すと起きること）。 */
  pressedLabel: string;
  /** 押したときに呼ぶ。 */
  onToggle: () => void;
}

/**
 * 押し下げで表示を切り替える 1 つのボタン。
 *
 * 押せないときは押し下げも外し、文言は押されていないときのものにする — 押せないボタンが
 * 「戻る」側の文言を出すと、押せない理由（切り替える先が無い）が読めなくなる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 押すと起きることを書いた文言を押し下げで入れ替え、押下中は塗りと aria-pressed で示すボタンを描く。
 * @graph-connects none
 */
export function StateToggleButton({
  pressed,
  idleLabel,
  pressedLabel,
  onToggle,
  disabled = false,
  ...rest
}: StateToggleButtonProps) {
  const shownPressed = pressed && !disabled;

  return (
    <Button
      {...rest}
      variant={shownPressed ? 'primary' : 'secondary'}
      size="sm"
      aria-pressed={shownPressed}
      disabled={disabled}
      onClick={onToggle}
    >
      {shownPressed ? pressedLabel : idleLabel}
    </Button>
  );
}
